import { useEffect, useRef, useState } from 'react';
import type { EncryptedQuiz, MediaRef } from '@quiz/shared';
import { prefetchMedia, openMedia, mediaQuestions, type PrefetchProgress } from '../lib/mediaCache.js';

/**
 * Download the night's media while everyone is still arriving.
 *
 * Runs from the holding area, which is the only window wide enough: measured,
 * a round of six 480p video questions is 900MB across 75 phones -- 40Mbps if
 * you deliver it in a three-minute round break, 6Mbps across a twenty-minute
 * door. The first number is optimistic for a pub access point. The second is
 * nothing.
 */
export function useMediaPrefetch(
  quiz: EncryptedQuiz,
  eventId: string,
  token: string,
  audioMode: 'HOST' | 'PLAYERS',
) {
  const [progress, setProgress] = useState<PrefetchProgress | null>(null);
  // A ref, not state, and for the same reason the home page's alert set is one:
  // writing progress into state re-runs an effect keyed on it, which would
  // restart the download it is reporting on.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    if (mediaQuestions(quiz, audioMode).length === 0) return;
    started.current = true;
    const abort = new AbortController();
    void prefetchMedia(quiz, eventId, token, audioMode, setProgress, abort.signal);
    return () => abort.abort();
  }, [quiz, eventId, token, audioMode]);

  return progress;
}

/**
 * Turn one question's sealed media into a URL, once the key has arrived.
 *
 * Returns null while there is no key -- which is every moment before the host
 * opens the question, however long the bytes have been on the phone.
 */
export function useMediaUrl(
  eventId: string,
  questionId: string | undefined,
  media: MediaRef | undefined,
  key: string | null | undefined,
  token: string,
): { url: string | null; state: 'idle' | 'loading' | 'ready' | 'error' } {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  useEffect(() => {
    if (!questionId || !media || !key) {
      setUrl(null);
      setState('idle');
      return;
    }
    let cancelled = false;
    let made: string | null = null;
    setState('loading');
    openMedia(eventId, questionId, token, key, media.mimeType)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        made = u;
        setUrl(u);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
      // Release it on the way out. Forty questions' worth of decrypted video
      // held as live object URLs is how a phone runs out of memory mid-quiz.
      if (made) URL.revokeObjectURL(made);
    };
  }, [eventId, questionId, media, key, token]);

  return { url, state };
}
